import { useState } from 'react';
import { apiPost } from '../../services/apiClient';
import { useAdminAuth } from '../../context/AdminAuthContext';

const AdminSecurity = () => {
  const { user, refresh } = useAdminAuth();
  const [setupData, setSetupData] = useState(null);
  const [token, setToken] = useState('');
  const [disableToken, setDisableToken] = useState('');
  const [status, setStatus] = useState('');

  const startSetup = async () => {
    setStatus('');
    try {
      const data = await apiPost('/api/admin/2fa/setup', {});
      setSetupData(data);
    } catch (err) {
      setStatus(err.message || 'Failed to start 2FA setup.');
    }
  };

  const enable2FA = async () => {
    setStatus('');
    try {
      await apiPost('/api/admin/2fa/enable', { token });
      setToken('');
      setSetupData(null);
      await refresh();
      setStatus('Two-factor authentication enabled.');
    } catch (err) {
      setStatus(err.message || 'Failed to enable 2FA.');
    }
  };

  const disable2FA = async () => {
    setStatus('');
    try {
      await apiPost('/api/admin/2fa/disable', { token: disableToken });
      setDisableToken('');
      await refresh();
      setStatus('Two-factor authentication disabled.');
    } catch (err) {
      setStatus(err.message || 'Failed to disable 2FA.');
    }
  };

  const isEnabled = Boolean(user?.twoFactorEnabled);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Settings</p>
        <h1 className="admin-page-title text-2xl font-semibold text-slate-900 mt-2">Security</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage two-factor authentication for your admin account.
        </p>
      </div>

      {status && (
        <div className="admin-card rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600 shadow-sm">
          {status}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        <div className="admin-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Two-factor status</h2>
          <p className="text-sm text-slate-500 mt-1">
            {isEnabled
              ? 'Two-factor authentication is enabled for your account.'
              : 'Enable two-factor authentication for extra security.'}
          </p>

          {!isEnabled && (
            <div className="mt-4">
              <button
                type="button"
                onClick={startSetup}
                className="rounded-full bg-slate-900 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white"
              >
                Start setup
              </button>
            </div>
          )}

          {setupData && !isEnabled && (
            <div className="mt-6 space-y-4">
              {setupData.qrCodeDataUrl ? (
                <img
                  src={setupData.qrCodeDataUrl}
                  alt="2FA QR code"
                  className="h-40 w-40 rounded-xl border border-slate-200"
                />
              ) : null}
              <div className="text-xs text-slate-500">
                Scan the QR code in your authenticator app or enter the secret:
                <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {setupData.secret}
                </div>
              </div>
              <input
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter 6-digit code"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={enable2FA}
                className="rounded-full bg-slate-900 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white"
              >
                Verify & enable
              </button>
            </div>
          )}
        </div>

        <div className="admin-card rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Disable 2FA</h2>
          <p className="text-sm text-slate-500 mt-1">
            Enter a valid code to disable two-factor authentication.
          </p>
          <div className="mt-4 space-y-3">
            <input
              value={disableToken}
              onChange={(e) => setDisableToken(e.target.value)}
              placeholder="Enter 6-digit code"
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              disabled={!isEnabled}
            />
            <button
              type="button"
              onClick={disable2FA}
              className="rounded-full border border-slate-200 px-4 py-2 text-xs uppercase tracking-[0.2em] text-slate-500"
              disabled={!isEnabled}
            >
              Disable 2FA
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSecurity;

